import { faBuilding, faUser, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DialogContent, Grid, TextField,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';

import {
  FlexContainer, FormAddress, FormCountryIdentifier, FormElement, Question, SkeletonForm
} from 'components';
import { ModalProps } from 'hooks/ModalProvider';
import { EntityView } from 'models';
import { IdentifierView } from 'models/detail/IdentifierView';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { LegalEntityService } from 'services';
import { LEGAL_ENTITY_TYPES, STOCK_ROLES_WITH_INTERVENTION_MANAGER } from 'utils/constants';
import { FormHelper, translate, UserHelper } from 'utils/helpers';
import { ModalHeader } from './ModalHeader';
import { CustomActionButtons } from './_CustomActionButtons';

export const AddEquipmentOwnerModal = ({
                                         onSubmit,
                                         onClose,
                                         defaultValues = new EntityView(),
                                         equipmentOwnerId
                                       }: ModalProps<EntityView> & {
  defaultValues: EntityView,
  equipmentOwnerId?: string
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [identifierFields, setIdentifierFields] = useState<IdentifierView[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    clearErrors,
    formState: { errors }
  } = useForm<EntityView>({
    defaultValues: { ...defaultValues }
  });

  const formValues = watch();
  const canValidate = equipmentOwnerId && !formValues.validated && UserHelper.hasAccessRight(STOCK_ROLES_WITH_INTERVENTION_MANAGER);

  useEffect(() => {
    if (!equipmentOwnerId) {
      return;
    }
    setIsLoading(true);

    LegalEntityService.getLegalEntity(equipmentOwnerId, [LEGAL_ENTITY_TYPES.EQUIPMENT_OWNER])
        .then((response) => {
          Object.entries(response).forEach(([key, value]) => setValue(key as any, value));
          if (response.address && typeof response.address !== 'string') {
            setValue('address1' as any, response.address?.address1);
            setValue('address2' as any, response.address?.address2);
            setValue('city' as any, response.address?.city);
            setValue('country' as any, response.address?.country);
            setValue('postalCode' as any, response.address?.postalCode);
          }
        })
        .finally(() => { setIsLoading(false); });
  }, [equipmentOwnerId, setValue]);

  const isPerson = useCallback(() => formValues.ownerType === 'PERSON', [formValues.ownerType]);

  const handleValidateModalSubmit = (data) => {
    onSubmit({
      ...data,
      firstName: data.firstName || data.institutionName,
      institutionIdentifiers: identifierFields,
      institutionIdentifier: identifierFields && identifierFields[0]?.value,
      possibleTypes: [LEGAL_ENTITY_TYPES.EQUIPMENT_OWNER],
      address: data.address1 ? {
        address1: data.address1,
        address2: data.address2,
        postalCode: data.postalCode,
        city: data.city,
        country: data.country
      } : null
    });
  };

  const handleChange = useCallback((_, value) => {
    if (value === null) return;
    setValue('ownerType' as any, value);
    setValue('lastName' as any, '');
    setValue('institutionIdentifier' as any, '');
  }, [setValue]);

  return (
    <>
      <form autoComplete="off" style={{ overflowY: 'hidden' }} onSubmit={handleSubmit(handleValidateModalSubmit)}>
        <ModalHeader onClose={onClose}>
          <FontAwesomeIcon icon={faUserPlus} />
          {equipmentOwnerId ? translate('modalEquipmentOwner.editEquipmentOwner') : translate('modalEquipmentOwner.newEquipmentOwner')}
        </ModalHeader>

        {isLoading ? (
          <SkeletonForm />
          ) : (
            <>
              <DialogContent style={{ minWidth: '700px', padding: '10px' }}>
                <FlexContainer flexDirection="column">
                  <Question label={translate('modalEquipmentOwner.equipmentOwnerType')}>
                    <ToggleButtonGroup
                          aria-label={translate('modalEquipmentOwner.equipmentOwnerType')}
                          exclusive
                          size="medium"
                          style={{ marginBottom: '1rem' }}
                          value={formValues.ownerType}
                          onChange={handleChange}
                      >
                      <ToggleButton aria-label={translate('common.institution')} disabled={!!equipmentOwnerId} value={'INSTITUTION'}>
                        <FontAwesomeIcon icon={faBuilding} />
                        {translate('common.institution')}
                      </ToggleButton>
                      <ToggleButton aria-label={translate('common.physicalPerson')} disabled={!!equipmentOwnerId} value={'PERSON'}>
                        <FontAwesomeIcon icon={faUser} />
                        {translate('common.physicalPerson')}
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {isPerson() ? (
                      <FormElement>
                        <Grid container>
                          <Grid item style={{ paddingRight: '10px' }}>
                            <Controller
                                    control={control}
                                    name="firstName"
                                    render={({ field }) => (
                                      <TextField
                                            autoComplete="new-password"
                                            error={!!errors.firstName}
                                            label={translate('common.firstName')}
                                            required
                                            {...field}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                          </Grid>
                          <Grid item style={{ paddingLeft: '10px' }}>
                            <Controller
                                    control={control}
                                    name="lastName"
                                    render={({ field }) => (
                                      <TextField
                                            autoComplete="new-password"
                                            error={!!errors.lastName}
                                            label={translate('common.lastName')}
                                            required
                                            {...field}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                          </Grid>
                        </Grid>
                      </FormElement>
                      ) : (
                        <FormCountryIdentifier
                              clearErrors={clearErrors}
                              control={control}
                              getValues={getValues}
                              identifierFields={identifierFields}
                              setIdentifierFields={setIdentifierFields}
                              setValue={setValue}
                              watch={watch}
                          />
                      )}

                    <Controller
                          control={control}
                          name="email"
                          render={({ field }) => (
                            <TextField
                                  autoComplete="new-password"
                                  error={!!errors.email}
                                  helperText={errors.email?.message}
                                  label={translate('common.email')}
                                  {...field}
                              />
                          )}
                          rules={{
                            validate: value => FormHelper.validateEmail(value)
                          }}
                      />

                    {!isPerson() && (
                      <Controller
                              control={control}
                              name="contactName"
                              render={({ field }) => (
                                <TextField
                                      autoComplete="new-password"
                                      label={translate('common.contactName')}
                                      {...field}
                                  />
                              )}
                          />
                      )}

                    <FormAddress
                          control={control}
                          getValues={getValues}
                          isRequired
                          setValue={setValue}
                          watch={watch}
                          withCountry={isPerson()}
                      />
                    {/* This input is only used for forcing disabling autocomplete in chrome browser */}
                    <input name="fakeInputForForcingDisablingAutocompleteChromee" style={{ display: 'none' }} type="text" />
                  </Question>
                </FlexContainer>
              </DialogContent>

              <CustomActionButtons
                    forValidation={canValidate}
                    isLoading={isLoading}
                    onClose={onClose}
                />
            </>
          )}
      </form>
    </>
  );
};
